import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useContext, useState, useRef, Children, cloneElement } from 'react';
import debounce from '../../utils/debounce.js';
import TabsContext from './TabsContext.js';
import { matches, match } from '../../utils/keyboard/match.js';
import { ArrowRight, ArrowLeft, Home, End } from '../../utils/keyboard/keys.js';
import { TabListContainer, NavButton, TabListScrollContainer, TabList as TabList$2 } from './Tabs.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useEffectOnce from '../../hooks/useEffectOnce.js';
import useIsomorphicEffect from '../../hooks/useIsomorphicEffect.js';
import Icon from '../Icon/Icon.js';
import { ICON_UI_CHEVRON_LEFT } from '../../icons/ICON_UI_CHEVRON_LEFT.js';

var _excluded = ["children", "fullWidth"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var getNextIndex = function getNextIndex(event, total, index) {
  switch (true) {
    case match(event, ArrowRight):
      return (index + 1) % total;
    case match(event, ArrowLeft):
      return (total + index - 1) % total;
    case match(event, Home):
      return 0;
    case match(event, End):
      return total - 1;
    default:
      return index;
  }
};
var isElementInViewport = function isElementInViewport(el) {
  var rect = el.getBoundingClientRect();
  return rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
};
var TabList = function TabList(_ref) {
  var children = _ref.children,
    fullWidth = _ref.fullWidth,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useContext = useContext(TabsContext),
    activeIndex = _useContext.activeIndex,
    setActiveIndex = _useContext.setActiveIndex,
    selectedIndex = _useContext.selectedIndex,
    setSelectedIndex = _useContext.setSelectedIndex;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isScrollable = _useState2[0],
    setIsScrollable = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    scrollLeft = _useState4[0],
    setScrollLeft = _useState4[1];
  var ref = useRef();
  var tabs = useRef([]);
  function _onKeyDown(event) {
    if (matches(event, [ArrowRight, ArrowLeft, Home, End])) {
      var _tabs$current$nextInd;
      event.preventDefault();
      var activeTabs = tabs.current.filter(function (tab) {
        return !tab.disabled;
      });
      var currentIndex = activeTabs.indexOf(tabs.current[activeIndex]);
      var nextIndex = tabs.current.indexOf(activeTabs[getNextIndex(event, activeTabs.length, currentIndex)]);
      setActiveIndex(nextIndex);
      (_tabs$current$nextInd = tabs.current[nextIndex]) === null || _tabs$current$nextInd === void 0 || _tabs$current$nextInd.focus();
    }
  }
  function leftNav() {
    if (ref.current) {
      setScrollLeft(Math.max(scrollLeft - ref.current.scrollWidth / tabs.current.length * 3, 0));
    }
  }
  function rightNav() {
    if (ref.current) {
      setScrollLeft(Math.min(scrollLeft + ref.current.scrollWidth / tabs.current.length * 3, ref.current.scrollWidth - ref.current.clientWidth));
    }
  }
  var isNextButtonVisible = ref.current ? scrollLeft + ref.current.clientWidth < ref.current.scrollWidth : false;
  var isPreviousButtonVisible = ref.current ? isScrollable && scrollLeft > 0 : false;

  // Scroll selected tab into view on load
  useEffectOnce(function () {
    var tab = tabs.current[selectedIndex];
    if (tab && isElementInViewport(tab === null || tab === void 0 ? void 0 : tab.parentElement)) {
      tab.scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
    }
  });

  // Select first available tab if selected tab is disabled
  useEffectOnce(function () {
    if (tabs.current[selectedIndex].disabled) {
      var activeTabs = tabs.current.filter(function (tab) {
        return !tab.disabled;
      });
      if (activeTabs.length > 0) {
        var tab = activeTabs[0];
        setSelectedIndex(tabs.current.indexOf(tab));
      }
    }
  });
  useIsomorphicEffect(function () {
    if (ref.current) {
      setIsScrollable(ref.current.scrollWidth > ref.current.clientWidth);
    }
    function handler() {
      if (ref.current) {
        setIsScrollable(ref.current.scrollWidth > ref.current.clientWidth);
      }
    }
    var debouncedHandler = debounce(handler, 200);
    window.addEventListener('resize', debouncedHandler);
    return function () {
      window.removeEventListener('resize', debouncedHandler);
    };
  }, []);

  // Updates scroll location for all scroll behaviors.
  useIsomorphicEffect(function () {
    if (scrollLeft !== null && ref.current) {
      ref.current.scrollLeft = scrollLeft;
    }
  }, [scrollLeft]);

  // Scroll edge tabs to a more centered position
  useIsomorphicEffect(function () {
    if (!isScrollable || !ref.current) {
      return;
    }
    var tab = tabs.current[selectedIndex];
    if (tab) {
      var _tab$getBoundingClien = tab.getBoundingClientRect(),
        tabWidth = _tab$getBoundingClien.width;
      var start = tab.offsetLeft;
      var end = tab.offsetLeft + tabWidth;
      var scrollRange = ref.current.clientWidth * 0.2;
      var startThreshold = ref.current.scrollLeft + scrollRange;
      var endThreshold = ref.current.scrollLeft + ref.current.clientWidth - scrollRange;

      // The beginning of the tab is scroll range
      if (start < startThreshold) {
        setScrollLeft(Math.max(start - scrollRange, 0));
      }

      // The end of the tab is scroll range
      if (end > endThreshold) {
        setScrollLeft(Math.min(end + scrollRange - ref.current.clientWidth, ref.current.scrollWidth - ref.current.clientWidth));
      }
    }
  }, [selectedIndex, isScrollable]);
  return /*#__PURE__*/jsxs(TabListContainer, {
    children: [isPreviousButtonVisible && /*#__PURE__*/jsx(NavButton, {
      "aria-hidden": "true",
      onClick: function onClick() {
        return leftNav();
      },
      children: /*#__PURE__*/jsx(Icon, {
        path: ICON_UI_CHEVRON_LEFT,
        size: "xs"
      })
    }), /*#__PURE__*/jsx(TabListScrollContainer, {
      ref: ref,
      children: /*#__PURE__*/jsx(TabList$2, _objectSpread(_objectSpread({
        role: "tablist",
        $fullWidth: fullWidth && !isScrollable,
        onKeyDown: function onKeyDown(event) {
          return _onKeyDown(event);
        }
      }, rest), {}, {
        children: Children.map(children, function (child, index) {
          if (child) {
            return /*#__PURE__*/cloneElement(child, {
              index: index,
              $fullWidth: fullWidth && !isScrollable,
              ref: function ref(node) {
                tabs.current[index] = node;
              }
            });
          }
          return null;
        })
      }))
    }), isNextButtonVisible && /*#__PURE__*/jsx(NavButton, {
      "aria-hidden": "true",
      onClick: function onClick() {
        return rightNav();
      },
      $right: true,
      children: /*#__PURE__*/jsx(Icon, {
        path: ICON_UI_CHEVRON_LEFT,
        size: "xs"
      })
    })]
  });
};
TabList.defaultProps = {
  fullWidth: false
};
var TabList$1 = TabList;

export { TabList$1 as default };
//# sourceMappingURL=TabList.js.map
